import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Circle,
  Cpu,
  FileText,
  GearSix,
  Lightning,
  Robot,
  WarningCircle
} from '@phosphor-icons/react';

/**
 * ConfigCompleteness - Visual progress indicator for agent configuration
 * 
 * Shows completion status across key areas:
 * - Identity (name, description)
 * - Inputs (accepted inputs)
 * - Capabilities (required capabilities)
 * - Logic (system prompt, workflow)
 * - Output (expected outputs)
 * - Configuration (LLM binding)
 */
const ConfigCompleteness = ({ agentConfig, onSectionClick }) => {
  const sections = useMemo(() => {
    // Check if this is a template-installed agent (MCP compliant)
    const isFromTemplate = agentConfig.source_type === 'template' || agentConfig.installed_from_template;
    
    const checks = [
      {
        id: 'identity',
        label: 'Identity',
        icon: Robot,
        checks: [
          { label: 'Agent name', complete: !!agentConfig.name?.trim() },
          { label: 'Description', complete: !!agentConfig.description?.trim() }
        ]
      },
      {
        id: 'inputs',
        label: 'Inputs',
        icon: FileText,
        checks: [
          { label: 'Input types defined', complete: (agentConfig.accepted_inputs?.length || 0) > 0 }
        ]
      },
      {
        id: 'capabilities',
        label: 'Capabilities',
        icon: Cpu,
        checks: [
          { label: 'Required capabilities', complete: (agentConfig.required_capabilities?.length || 0) > 0 }
        ]
      },
      {
        id: 'logic',
        label: 'Logic',
        icon: Lightning,
        checks: [
          { label: 'System prompt', complete: !!agentConfig.system_prompt?.trim() || !!agentConfig.agent_config?.config?.system_prompt }
        ]
      },
      {
        id: 'output',
        label: 'Output',
        icon: FileText,
        checks: [
          { label: 'Output defined', complete: (agentConfig.expected_outputs?.length || 0) > 0 || (agentConfig.output_schema?.types?.length || 0) > 0 }
        ]
      },
      {
        id: 'config',
        label: 'Configuration',
        icon: GearSix,
        checks: [
          { label: 'LLM configured', complete: !!agentConfig.llm_model || !!agentConfig.capability_bindings?.llm_chat || isFromTemplate }
        ]
      }
    ];

    return checks.map(section => ({
      ...section,
      completedCount: section.checks.filter(c => c.complete).length,
      totalCount: section.checks.length,
      isComplete: section.checks.every(c => c.complete),
      hasPartial: section.checks.some(c => c.complete) && !section.checks.every(c => c.complete)
    }));
  }, [agentConfig]);

  const totalCompleted = sections.reduce((sum, s) => sum + s.completedCount, 0);
  const totalChecks = sections.reduce((sum, s) => sum + s.totalCount, 0);
  const overallPercent = Math.round((totalCompleted / totalChecks) * 100);

  return (
    <div className="p-4">
      {/* Overall Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-400">Configuration Progress</span>
          <span className={`text-sm font-bold ${
            overallPercent === 100 ? 'text-emerald-400' : 
            overallPercent >= 50 ? 'text-amber-400' : 'text-slate-400'
          }`}>
            {overallPercent}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${overallPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`h-full rounded-full ${
              overallPercent === 100 
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                : overallPercent >= 50
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                  : 'bg-gradient-to-r from-slate-500 to-slate-400'
            }`}
          />
        </div>
      </div>

      {/* Section Checklist */}
      <div className="space-y-2">
        {sections.map((section, idx) => (
          <motion.button
            key={section.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
            onClick={() => onSectionClick?.(section.id)}
            className={`
              w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all
              ${section.isComplete 
                ? 'bg-emerald-500/5 hover:bg-emerald-500/10' 
                : section.hasPartial
                  ? 'bg-amber-500/5 hover:bg-amber-500/10'
                  : 'bg-white/[0.02] hover:bg-white/5'
              }
            `}
          >
            {/* Status Icon */}
            <div className={`p-1.5 rounded-md ${
              section.isComplete 
                ? 'bg-emerald-500/20' 
                : section.hasPartial
                  ? 'bg-amber-500/20'
                  : 'bg-slate-500/20'
            }`}>
              {section.isComplete ? (
                <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              ) : section.hasPartial ? (
                <WarningCircle className="w-3.5 h-3.5 text-amber-400" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-slate-500" />
              )}
            </div>

            {/* Section Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${
                  section.isComplete ? 'text-emerald-400' : 
                  section.hasPartial ? 'text-amber-400' : 'text-slate-400'
                }`}>
                  {section.label}
                </span>
                <span className="text-xs text-slate-500">
                  {section.completedCount}/{section.totalCount}
                </span>
              </div>
              
              {/* Individual Checks (collapsed) */}
              {!section.isComplete && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {section.checks.filter(c => !c.complete).map((check, i) => (
                    <span 
                      key={i}
                      className="text-[10px] text-slate-500 bg-slate-500/10 px-1.5 py-0.5 rounded"
                    >
                      {check.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.button>
        ))}
      </div>

      {/* Ready to Deploy Indicator */}
      {overallPercent >= 75 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`
            mt-4 p-3 rounded-lg text-center
            ${overallPercent === 100 
              ? 'bg-emerald-500/10 border border-emerald-500/20' 
              : 'bg-amber-500/10 border border-amber-500/20'
            }
          `}
        >
          {overallPercent === 100 ? (
            <>
              <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
              <span className="text-sm text-emerald-400 font-medium">Ready to Deploy</span>
            </>
          ) : (
            <>
              <Lightning className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <span className="text-sm text-amber-400 font-medium">Almost Ready</span>
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default ConfigCompleteness;
